import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { contentBorders } from '../../theme/createContentTheme';

interface DialogHeaderProps {
  /** id for the title, referenced by the Dialog's `aria-labelledby`. */
  id: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: SvgIconComponent;
  onClose?: () => void;
  /** Disable the close button (e.g. while a save is in flight). */
  closeDisabled?: boolean;
}

/**
 * Dialog header from the ESG/ZERO pattern: accent icon square, title, a short
 * purpose line and a close button, separated from the body by a hairline.
 * Pair with `DIALOG_CONTENT_SX` / `DIALOG_ACTIONS_SX` (constants/dialog.styles)
 * for matching padding.
 */
const DialogHeader: React.FC<DialogHeaderProps> = ({ id, title, subtitle, icon: Icon, onClose, closeDisabled }) => {
  const theme = useTheme();
  const borders = contentBorders(theme);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        px: { xs: 2.5, sm: 3 },
        pt: { xs: 2.5, sm: 3 },
        pb: 2.5,
        borderBottom: `1px solid ${borders.default}`,
      }}
    >
      {Icon && (
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${CONTENT_SHAPE.sm}px`,
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
            color: 'primary.main',
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography id={id} component="h2" variant="h6" sx={{ m: 0 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: '60ch' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {onClose && (
        <IconButton
          onClick={onClose}
          disabled={closeDisabled}
          aria-label="Close"
          sx={{ mt: -0.5, mx: -0.5, color: 'text.secondary' }}
        >
          <CloseRounded />
        </IconButton>
      )}
    </Box>
  );
};

export default DialogHeader;
